import styled, { keyframes, css } from 'styled-components';

const theme = {
  bg: '#1C1E2A',
  surface: '#252836',
  surfaceHover: '#2d3040',
  primary: '#50fa7b',
  secondary: '#8be9fd',
  accent: '#ff79c6',
  text: '#f8f8f2',
  textSecondary: '#bd93f9',
  border: '#44475a',
  danger: '#ff5555',
  warning: '#f1fa8c',
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

export const ModalWrapper = styled.div`
  background-color: ${theme.surface};
  color: ${theme.text};
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.2s ease-out;
`;

export const ModalHeader = styled.header`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    color: ${theme.text};
  }
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.textSecondary};
  font-size: 1.8rem;
  cursor: pointer;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${theme.accent};
  }
`;

export const ModalBody = styled.div`
  padding: 2rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: ${theme.textSecondary};
  font-weight: 600;
  border-bottom: 1px solid ${theme.border};
  padding-bottom: 0.5rem;
`;

export const ButtonGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

export const TypeButton = styled.button`
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${theme.border};
  transition: all 0.2s ease;
  background-color: ${({ isSelected }) => isSelected ? theme.accent : theme.surfaceHover};
  color: ${({ isSelected }) => isSelected ? '#fff' : theme.text};
  
  &:hover:not(:disabled) {
    border-color: ${theme.accent};
    color: #fff;
  }
`;

export const InfoBox = styled.div`
  background-color: ${theme.bg};
  border: 1px solid ${theme.border};
  border-left: 4px solid ${theme.secondary};
  padding: 1.2rem;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    font-size: 1rem;
    color: ${theme.textSecondary};
  }

  strong {
    font-size: 1.5rem;
    color: ${theme.primary};
  }
`;

export const ContractList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid ${theme.border};
  border-radius: 8px;
`;

export const ContractItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: ${({ isSelected }) => isSelected ? `${theme.accent}30` : 'transparent'};
  border-bottom: 1px solid ${theme.border};

  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: ${theme.surfaceHover};
  }
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-size: 0.9rem;
    font-weight: 500;
    color: ${theme.textSecondary};
  }
`;

export const Input = styled.input`
  width: 100%;
  height: 50px;
  background-color: ${theme.bg};
  border: 1px solid ${theme.border};
  border-radius: 8px;
  color: ${theme.text};
  font-size: 1.5rem;
  font-weight: 700;
  padding: 0 1rem;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.accent};
  }
`;

export const Summary = styled.div`
  padding: 1rem;
  margin-top: 0.5rem;
  border-radius: 8px;
  background-color: ${theme.bg};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  div {
    display: flex;
    justify-content: space-between;
    font-size: 0.9rem;
    
    span:first-child {
      color: ${theme.textSecondary};
    }
    span:last-child {
      font-weight: 600;
    }
  }

  .net-value {
    color: ${theme.primary};
    font-size: 1.1rem;
  }
`;


export const ModalFooter = styled.footer`
  padding: 1.5rem 2rem;
  border-top: 1px solid ${theme.border};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
`;

export const ActionButton = styled.button`
  padding: 0.8rem 1.8rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  ${({ variant }) => variant === 'primary' && css`
    background-color: ${theme.primary};
    color: #333;
    &:hover:not(:disabled) {
      filter: brightness(1.1);
    }
  `}

  ${({ variant }) => variant === 'secondary' && css`
    background-color: ${theme.border};
    color: ${theme.text};
    &:hover:not(:disabled) {
      background-color: ${theme.surfaceHover};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DisabledMessage = styled.div`
    text-align: center;
    font-size: 1.2rem;
    color: ${theme.warning};
`;